import { create } from "zustand";
import { triggerSelfHealing } from "@/ai/selfHealing/triggerEngine"; // 🔥 ADDED
import { persistSave, persistLoad } from "@/persistence/persistenceEngine";
import { eventBus } from "@/runtime/eventBus";
import { SyncLedger } from "@/persistence/syncLedger";
import { CURRENT_SCHEMA_VERSION } from "@/persistence/storageAdapter";
import { getCanonicalSchema } from "@/core/schema/canonicalProvider";
import type { NuvraSchema } from "@/core/schema/nuvraSchema";

/* =========================================================
TYPES
========================================================= */

export interface CanvasBlock {
  id: string;
  componentId: string;
  name: string;
  category: string;
}

export interface DataModel {
  id: string;
  name: string;
  fields: { name: string; type: string }[];
}

export interface ProjectSettings {
  name: string;
  type: string;
  description: string;
  theme: string;
}

export interface ProjectFile {
  path: string;
  content: string;
}

interface ProjectSnapshot {
  blocks:        CanvasBlock[];
  pages:         string[];
  schema:        DataModel[];
  settings:      ProjectSettings;
  workflows:     string[];
  files:         ProjectFile[];
  /**
   * Phase 3B - Canonical undo snapshot.
   * When present, undo/redo restores this canonical schema directly (via
   * schema:undo_canonical_available / schema:redo_canonical_available), allowing
   * component props, model relations, and workflow steps to survive undo/redo.
   * Absent on legacy snapshots taken before Phase 3B - those fall back to the
   * existing store-reconstruction path (schema:staleness_detected → rebuildFromStore).
   */
  nuvraSnapshot?: NuvraSchema;
}

/* =========================================================
STATE
========================================================= */

export interface ProjectState {
  projectId: string | null;
  pages: string[];
  blocks: CanvasBlock[];
  schema: DataModel[];
  workflows: string[];
  designSystem: Record<string, string>;
  settings: ProjectSettings;

  files: ProjectFile[];

  saveStatus: "idle" | "saving" | "saved" | "error";
  isDirty: boolean;

  logs: {
    message: string;
    level?: string;
    timestamp: number;
  }[];

  _undoStack: ProjectSnapshot[];
  _redoStack: ProjectSnapshot[];

  loadProject: (projectId: string) => void;
  saveProject: () => void;

  updateBlocks: (blocks: CanvasBlock[]) => void;
  updateSchema: (schema: DataModel[]) => void;
  updateSettings: (settings: Partial<ProjectSettings>) => void;
  updatePages: (pages: string[]) => void;
  updateWorkflows: (workflows: string[]) => void;
  updateFiles: (files: ProjectFile[]) => void;

  setSaveStatus: (status: ProjectState["saveStatus"]) => void;
  setDirty: (dirty: boolean) => void;

  resetProject: () => void;

  pushSnapshot: () => void;
  getSnapshot: () => ProjectSnapshot;
  restoreSnapshot: (snapshot: ProjectSnapshot) => void;

  undo: () => void;
  redo: () => void;

  restoreFromCloud: (payload: unknown) => void;

  addLog: (log: { message: string; level?: string }) => void;

  /**
   * Atomic canonical projection write - bridge use only.
   *
   * Applies all derived projections from a NuvraSchema in a single setState call.
   * Does NOT push a snapshot, does NOT clear the redo stack, does NOT trigger
   * self-healing. This is the only safe way for schemaBridge to sync projections
   * without creating a mutation storm.
   */
  _setFromCanonical: (fields: {
    pages?:        string[];
    blocks?:       CanvasBlock[];
    schema?:       DataModel[];
    workflows?:    string[];
    settings?:     Partial<ProjectSettings>;
    designSystem?: Record<string, string>;
  }) => void;
}

/* =========================================================
DEFAULTS
========================================================= */

const defaultSettings: ProjectSettings = {
  name: "Untitled Project",
  type: "Website",
  description: "",
  theme: "dark",
};

/* =========================================================
SNAPSHOT BUILDER
========================================================= */

function buildSnapshot(state: ProjectState): ProjectSnapshot {
  return {
    blocks:        [...state.blocks],
    pages:         [...state.pages],
    schema:        [...state.schema],
    workflows:     [...state.workflows],
    settings:      { ...state.settings },
    files:         [...state.files],
    nuvraSnapshot: getCanonicalSchema() ?? undefined, // Phase 3B: canonical-first undo/redo
  };
}

/* =========================================================
STORE
========================================================= */

export const useProjectStore = create<ProjectState>((set, get) => ({
  projectId: null,
  pages: [],
  blocks: [],
  schema: [],
  workflows: [],
  designSystem: {},
  settings: { ...defaultSettings },

  files: [],

  saveStatus: "idle",
  isDirty: false,

  logs: [],

  _undoStack: [],
  _redoStack: [],

  /* ================= LOAD ================= */
  loadProject: (projectId: string) => {
    try {
      // Attempt local load via persistenceEngine (handles envelope + legacy formats)
      const data = persistLoad<Record<string, unknown>>(`project-${projectId}`);

      if (data) {
        set({
          projectId,
          pages:        (data.pages        as string[])           ?? [],
          blocks:       (data.blocks       as CanvasBlock[])      ?? [],
          schema:       (data.schema       as DataModel[])        ?? [],
          workflows:    (data.workflows    as string[])           ?? [],
          designSystem: (data.designSystem as Record<string, string>) ?? {},
          settings:     { ...defaultSettings, ...(data.settings as Partial<ProjectSettings>) },
          files:        (data.files        as ProjectFile[])      ?? [],
          saveStatus:   "saved",
          isDirty:      false,
          _undoStack:   [],
          _redoStack:   [],
        });
        const sequenceNum = (data.sequenceNum as number) ?? 0;
        eventBus.emit({ type: "persistence:loaded_from_local", projectId, sequenceNum });
        eventBus.emit({ type: "state:loaded", projectId });

        // Phase 2 canonical-first: if a canonical schema was persisted, offer it
        // to the bridge before initSchemaFromStore() is called from EditorLayout.
        if (data.canonicalSchema && typeof data.canonicalSchema === "object") {
          eventBus.emit({
            type:      "persistence:canonical_available",
            projectId,
            schema:    data.canonicalSchema as NuvraSchema,
          });
        }
      } else {
        // Nothing in local - initialize empty and signal bridge to attempt cloud restore
        set({
          projectId,
          pages:        [],
          blocks:       [],
          schema:       [],
          workflows:    [],
          designSystem: {},
          settings:     { ...defaultSettings },
          files:        [],
          saveStatus:   "idle",
          isDirty:      false,
          _undoStack:   [],
          _redoStack:   [],
        });
        // Bridge will listen for this and attempt Supabase fetch
        eventBus.emit({ type: "persistence:local_miss", projectId });
      }
    } catch {
      set({ projectId, saveStatus: "error" });
      eventBus.emit({ type: "persistence:error", reason: `loadProject failed for ${projectId}` });
    }
  },

  /* ================= SAVE ================= */
  saveProject: () => {
    const state = get();
    if (!state.projectId) return;

    set({ saveStatus: "saving" });

    try {
      // Assign sequence number BEFORE building payload (persists immediately in ledger)
      const sequenceNum = SyncLedger.nextSequence(state.projectId);

      const payload = {
        pages:           state.pages,
        blocks:          state.blocks,
        schema:          state.schema,
        workflows:       state.workflows,
        designSystem:    state.designSystem,
        settings:        state.settings,
        files:           state.files,
        lastSaved:       Date.now(),
        sequenceNum,
        schemaVersion:   CURRENT_SCHEMA_VERSION,
        canonicalSchema: getCanonicalSchema() ?? undefined,
      };

      // Local write via persistenceEngine (checksum-protected, synchronous)
      const ok = persistSave(`project-${state.projectId}`, payload);
      if (!ok) throw new Error("persistSave returned false");

      set({ saveStatus: "saved", isDirty: false });

      // Notify bridge via event bus (async cloud sync starts here)
      eventBus.emit({ type: "state:saved", projectId: state.projectId });

      triggerSelfHealing("SAVE");
    } catch (err) {
      set({ saveStatus: "error" });
      eventBus.emit({ type: "persistence:error", reason: `saveProject failed: ${err}` });
    }
  },

  /* ================= LOG ================= */
  addLog: ({ message, level = "info" }) => {
    const logs = get().logs;

    set({
      logs: [...logs, { message, level, timestamp: Date.now() }],
    });
  },

  /* ================= UPDATERS ================= */
  updateBlocks: (blocks) => {
    get().pushSnapshot();
    set({ blocks, isDirty: true, _redoStack: [] });
    triggerSelfHealing("PROJECT_CHANGE");
  },

  updateSchema: (schema) => {
    get().pushSnapshot();
    set({ schema, isDirty: true, _redoStack: [] });
    triggerSelfHealing("PROJECT_CHANGE");
  },

  updateSettings: (partial) => {
    get().pushSnapshot();
    set((s) => ({
      settings: { ...s.settings, ...partial },
      isDirty: true,
      _redoStack: [],
    }));
    triggerSelfHealing("PROJECT_CHANGE");
  },

  updatePages: (pages) => {
    get().pushSnapshot();
    set({ pages, isDirty: true, _redoStack: [] });
    triggerSelfHealing("PROJECT_CHANGE");
  },

  updateWorkflows: (workflows) => {
    get().pushSnapshot();
    set({ workflows, isDirty: true, _redoStack: [] });
    triggerSelfHealing("PROJECT_CHANGE");
  },

  updateFiles: (files) => {
    get().pushSnapshot();
    set({ files, isDirty: true, _redoStack: [] });
    triggerSelfHealing("PROJECT_CHANGE");
  },

  _setFromCanonical: (fields) => {
    set((s) => ({
      ...(fields.pages        !== undefined && { pages:        fields.pages }),
      ...(fields.blocks       !== undefined && { blocks:       fields.blocks }),
      ...(fields.schema       !== undefined && { schema:       fields.schema }),
      ...(fields.workflows    !== undefined && { workflows:    fields.workflows }),
      ...(fields.settings     !== undefined && { settings:     { ...s.settings, ...fields.settings } }),
      ...(fields.designSystem !== undefined && { designSystem: fields.designSystem }),
      isDirty: true,
    }));
    // No pushSnapshot(), no _redoStack clear, no triggerSelfHealing()
  },

  setSaveStatus: (status) => set({ saveStatus: status }),
  setDirty: (dirty) => set({ isDirty: dirty }),

  /* ================= SNAPSHOT ================= */
  pushSnapshot: () => {
    const snap = buildSnapshot(get());
    set((s) => ({
      _undoStack: [...s._undoStack.slice(-49), snap],
    }));
  },

  getSnapshot: () => buildSnapshot(get()),

  restoreSnapshot: (snapshot) => {
    if (!snapshot) return;

    set({
      blocks: snapshot.blocks,
      pages: snapshot.pages,
      schema: snapshot.schema,
      workflows: snapshot.workflows,
      settings: snapshot.settings,
      files: snapshot.files || [],
      isDirty: true,
    });
  },

  /* ================= HISTORY ================= */
  undo: () => {
    const state = get();
    if (!state._undoStack.length) return;

    const current = buildSnapshot(state);
    const prev = state._undoStack[state._undoStack.length - 1];

    set({
      blocks:     prev.blocks,
      pages:      prev.pages,
      schema:     prev.schema,
      workflows:  prev.workflows,
      settings:   prev.settings,
      files:      prev.files || [],
      isDirty:    true,
      _undoStack: state._undoStack.slice(0, -1),
      _redoStack: [...state._redoStack, current],
    });

    // Phase 3B: if the undo target snapshot carries a canonical schema, offer it to
    // the bridge BEFORE emitting staleness. The bridge pre-populates currentSchema so
    // rebuildFromStore() uses the canonical-first shortcut - preserving component props,
    // model relations, and workflow steps that are absent from flat store projections.
    if (prev.nuvraSnapshot) {
      eventBus.emit({
        type:      "schema:undo_canonical_available",
        projectId: state.projectId ?? "",
        schema:    prev.nuvraSnapshot,
      });
    }

    eventBus.emit({ type: "schema:staleness_detected", reason: "undo", projectId: state.projectId ?? undefined });
  },

  redo: () => {
    const state = get();
    if (!state._redoStack.length) return;

    const current = buildSnapshot(state);
    const next = state._redoStack[state._redoStack.length - 1];

    set({
      blocks:     next.blocks,
      pages:      next.pages,
      schema:     next.schema,
      workflows:  next.workflows,
      settings:   next.settings,
      files:      next.files || [],
      isDirty:    true,
      _undoStack: [...state._undoStack, current],
      _redoStack: state._redoStack.slice(0, -1),
    });

    // Phase 3B: same canonical-first pattern as undo.
    if (next.nuvraSnapshot) {
      eventBus.emit({
        type:      "schema:redo_canonical_available",
        projectId: state.projectId ?? "",
        schema:    next.nuvraSnapshot,
      });
    }

    eventBus.emit({ type: "schema:staleness_detected", reason: "redo", projectId: state.projectId ?? undefined });
  },

  /* ================= RESET ================= */
  resetProject: () =>
    set({
      projectId: null,
      pages: [],
      blocks: [],
      schema: [],
      workflows: [],
      designSystem: {},
      settings: { ...defaultSettings },
      files: [],
      saveStatus: "idle",
      isDirty: false,
      _undoStack: [],
      _redoStack: [],
      logs: [],
    }),

  /* ================= CLOUD RESTORE ================= */
  restoreFromCloud: (payload: unknown) => {
    const data = payload as Record<string, unknown>;
    if (!data) return;
    const projectId = data.projectId as string;
    if (!projectId) return;

    set({
      projectId,
      pages:        (data.pages        as string[])               ?? [],
      blocks:       (data.blocks       as CanvasBlock[])          ?? [],
      schema:       (data.schema       as DataModel[])            ?? [],
      workflows:    (data.workflows    as string[])               ?? [],
      designSystem: (data.designSystem as Record<string, string>) ?? {},
      settings:     { ...defaultSettings, ...(data.settings as Partial<ProjectSettings>) },
      files:        (data.files        as ProjectFile[])          ?? [],
      saveStatus:   "saved",
      isDirty:      false,
    });

    // Phase 2: if the cloud payload carries a canonical schema, offer it to the bridge
    // before schema:staleness_detected triggers rebuildFromStore().
    if (data.canonicalSchema && typeof data.canonicalSchema === "object") {
      eventBus.emit({
        type:      "persistence:canonical_available",
        projectId,
        schema:    data.canonicalSchema as NuvraSchema,
      });
    }

    // Canonical schema is stale after cloud restore - bridge must rebuild
    // (no-op if persistence:canonical_available already set currentSchema)
    eventBus.emit({ type: "schema:staleness_detected", reason: "cloud_restore", projectId });
  },
}));

// Listen for cloud restore events (emitted by PersistenceBridge after successful Supabase fetch)
eventBus.on("persistence:cloud_restore", (event) => {
  useProjectStore.getState().restoreFromCloud(event.snapshot);
});